import { useMutation, useQuery } from "@apollo/client";
import { ChangeEvent, FormEventHandler, MouseEvent, useState } from "react";
import {
  CreateTopicNewPageMutationMutation,
  CreateTopicNewPageMutationMutationVariables,
  GetUpcomingMeetingsQuery,
  GetUpcomingMeetingsQueryVariables,
} from "types/graphql-schema";

import createOrUpdateTopicMutation from "@apps/meeting-new/graphql/create-or-update-topic-mutation";
import { successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { ButtonSize } from "@components/button/button-constants";
import GraphqlError from "@components/error/graphql-error";
import Input from "@components/input/input";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import ModalTitle from "@components/modal/modal-title";
import { Select, SelectOption } from "@components/select/select";
import TextareaAutosize from "@components/textarea-autosize/textarea-autosize";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  getFormattedMeetingDateTimes,
} from "@helpers/helpers";
import { plainTextToTiptap } from "@helpers/tiptap-helpers";

import getUpcomingMeetingsQuery from "./graphql/get-upcoming-meetings-query";

// Helper to convert notes array to plain text for editing
const notesToPlainText = (notes: string | string[] | undefined): string => {
  if (!notes) return "";
  if (Array.isArray(notes)) {
    return notes.join("\n");
  }
  if (typeof notes === "string") {
    return notes;
  }
  return "";
};

const DialogLabel = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <label
    className={classNames(
      "text-gray-500 text-sm flex items-center gap-2 cursor-pointer font-medium w-24 shrink-0",
      className,
    )}
  >
    {children}
  </label>
);

const AddTopicToMeetingDialog = ({
  topicTitle,
  topicNotes,
  initialMeetingId,
  variables,
  onClose,
  onCreatedTopic,
}: {
  topicTitle: string;
  topicNotes?: string | string[];
  initialMeetingId?: number;
  variables: GetUpcomingMeetingsQueryVariables;
  onClose: () => void;
  onCreatedTopic: () => void;
}) => {
  const [isShowingInvalidForm, setIsShowingInvalidForm] = useState(false);

  // use to track changes between form and initial form
  const [form, setForm] = useState<{
    title: string;
    meetingId: number;
    notesText: string;
  }>({
    title: topicTitle,
    meetingId: initialMeetingId || 0,
    notesText: notesToPlainText(topicNotes),
  });

  let formInvalid = "";
  if (form.title.trim() === "") {
    formInvalid = "Enter a title";
  } else if (!form.meetingId) {
    formInvalid = "Select a meeting";
  }

  const { data, loading: loadingQuery } = useQuery<
    GetUpcomingMeetingsQuery,
    GetUpcomingMeetingsQueryVariables
  >(getUpcomingMeetingsQuery, {
    variables,

    onCompleted: (response) => {
      // Only auto-select if no initial meeting was provided
      if (!initialMeetingId) {
        const meetingId = response.meetings?.edges[0]?.node?.id;
        if (meetingId) {
          setForm((prev) => ({ ...prev, meetingId }));
        }
      }
    },
  });
  const meetings = data?.meetings ? assertEdgesNonNull(data?.meetings) : [];
  const options =
    meetings.length > 0
      ? meetings.map((meeting) => ({
          label: meeting.title,
          value: meeting.id,
          description:
            meeting.startDatetime && meeting.endDatetime
              ? `${getFormattedMeetingDateTimes(
                  meeting.startDatetime,
                  meeting.endDatetime,
                )}`
              : "Not scheduled",
        }))
      : [
          {
            label: "No meetings found",
            value: 0,
          },
        ];

  const handleClose = () => {
    onClose();
    setIsShowingInvalidForm(false);
  };

  const handleSubmitForm: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const [createTopic, { error, loading: createTopicLoading }] = useMutation<
    CreateTopicNewPageMutationMutation,
    CreateTopicNewPageMutationMutationVariables
  >(createOrUpdateTopicMutation);

  const handleClickSubmit = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (formInvalid) {
      setIsShowingInvalidForm(true);
      return;
    }

    const isFormalOneonone = variables.isFormalOneonone ?? false;
    const notesData = form.notesText.trim()
      ? plainTextToTiptap(form.notesText)
      : null;

    createTopic({
      variables: {
        title: form.title,
        meetingId: form.meetingId,
        ...(notesData && {
          includesIndividualNotes: isFormalOneonone,
          includesSharedNotes: !isFormalOneonone,
          ...(isFormalOneonone
            ? { individualNotes: JSON.stringify(notesData) }
            : { discussionNotes: JSON.stringify(notesData) }),
        }),
      },

      onCompleted: () => {
        setIsShowingInvalidForm(false);
        successNotificationVar({
          title: `Topic added to meeting.`,
          timeout: 3000,
        });
        onCreatedTopic();
      },
    });
  };

  const handleChangeTitle = (e: ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, title: e.target.value }));
  };

  const handleChangeMeeting = (opt: SelectOption<number>) => {
    setForm((prev) => ({ ...prev, meetingId: opt.value }));
  };

  const handleChangeNotes = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, notesText: e.target.value }));
  };

  return (
    <Modal onClose={handleClose}>
      <form className="flex flex-col gap-6 p-6" onSubmit={handleSubmitForm}>
        <ModalTitle onClose={handleClose}>
          <div className="flex items-center gap-2">
            Add a Topic to a Meeting
          </div>
        </ModalTitle>

        <div className="flex items-center gap-4 ">
          <DialogLabel className="mb-1">Meeting</DialogLabel>
          <div className="w-full flex-1">
            {loadingQuery ? (
              <div className="flex items-center text-sm text-gray-400 gap-2 min-h-[38px]">
                <Loading mini size={4} />
                Loading meetings...
              </div>
            ) : (
              <Select<number>
                value={form.meetingId}
                disabled={loadingQuery || meetings.length === 0}
                options={options}
                onChange={handleChangeMeeting}
              />
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <DialogLabel className="mb-1">Topic*</DialogLabel>
          <div className="w-full flex-1">
            <Input
              value={form.title}
              disabled={loadingQuery}
              onChange={handleChangeTitle}
            />
          </div>
        </div>

        {form.notesText && (
          <div className="flex gap-4">
            <DialogLabel className="self-start">Notes</DialogLabel>
            <div className="w-full flex-1">
              <TextareaAutosize
                value={form.notesText}
                onChange={handleChangeNotes}
                disabled={loadingQuery}
                placeholder="Add notes (one per line)..."
                className="w-full text-sm text-gray-700 bg-white border border-gray-300 rounded-md p-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                minRows={3}
              />
            </div>
          </div>
        )}

        <div className="mt-6 text-sm">
          {formInvalid && isShowingInvalidForm && (
            <div className="text-red-700 empty:hidden mb-4">{formInvalid}</div>
          )}
          <div className="gap-4 grid grid-cols-2">
            <Button
              disabled={createTopicLoading || loadingQuery}
              type="submit"
              theme="primary"
              onClick={handleClickSubmit}
              className="flex items-center gap-2"
              size={ButtonSize.large}
            >
              {createTopicLoading ? "Adding Topic" : "Add Topic"}
              {createTopicLoading && <Loading mini size="4" color="white" />}
            </Button>
            <Button
              disabled={createTopicLoading}
              onClick={handleClose}
              type="button"
              text="Cancel"
              size={ButtonSize.large}
            />
          </div>
          {error && (
            <div className="mt-4">
              <GraphqlError
                whatDidNotWork="The topic could not be added to the meeting."
                error={error}
              />
            </div>
          )}
        </div>
      </form>
    </Modal>
  );
};

export default AddTopicToMeetingDialog;
